import { useEffect, useMemo, useState } from 'react';
import { authFetch } from '@/auth/token';
import {
  artifactFileUrl,
  IFRAME_SANDBOX,
  parsePreviewUrl,
  primaryArtifact,
  shareUrlFromTree,
  hasInfraArtifacts,
  type OutputNode,
} from '../outputFiles';
import DeployUpsell from './DeployUpsell';

type Props = {
  tree: OutputNode[];
  outcomeId?: string | null;
  outcomeSlug?: string | null;
};

export default function ReviewPane({ tree, outcomeId = null, outcomeSlug = null }: Props) {
  const node = useMemo(() => primaryArtifact(tree), [tree]);
  const shareUrl = useMemo(() => shareUrlFromTree(tree), [tree]);
  const showDeploy = useMemo(() => hasInfraArtifacts(tree), [tree]);
  const previewUrl = node?.name === 'preview.json' ? parsePreviewUrl(node.content) : null;
  const [toast, setToast] = useState<string | null>(null);

  const copyShare = () => {
    if (!shareUrl) return;
    void navigator.clipboard.writeText(shareUrl).then(
      () => {
        setToast('Share link copied');
        setTimeout(() => setToast(null), 1600);
      },
      () => setToast('Copy failed'),
    );
  };

  return (
    <div data-testid="review-pane" className="flex-1 flex flex-col overflow-hidden">
      <div className="shrink-0 px-5 py-3 border-b border-border flex items-center gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-textp">Review</div>
          <div className="text-[11px] text-texts">
            {node ? node.name : 'HTML report from the last run'}
          </div>
        </div>
        <div className="ml-auto shrink-0 flex items-center gap-2">
          {shareUrl && (
            <button
              type="button"
              onClick={copyShare}
              className="flex items-center gap-1.5 bg-surface2 hover:bg-border text-textp text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap"
            >
              <i className="ri-share-forward-line" /> Copy share link
            </button>
          )}
          {(shareUrl || previewUrl) && (
            <a
              href={shareUrl || previewUrl || '#'}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 bg-surface2 hover:bg-border text-textp text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap"
            >
              <i className="ri-external-link-line" /> Open in new tab
            </a>
          )}
          {showDeploy && <DeployUpsell slug={outcomeSlug || outcomeId} outcomeId={outcomeId} />}
        </div>
      </div>
      {node ? (
        <ReviewFrame node={node} outcomeId={outcomeId} previewUrl={previewUrl} />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center text-texts px-6">
          <i className="ri-eye-line text-4xl mb-3" />
          <div className="text-sm text-textp font-medium">No HTML report yet</div>
          <div className="text-xs mt-1 max-w-sm">
            Click Run. The playbook writes an HTML shell for CSV and JSON so you can review it here.
          </div>
        </div>
      )}
      {toast && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 bg-textp text-bg text-xs font-medium px-3 py-1.5 rounded-md z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

function ReviewFrame({
  node,
  outcomeId,
  previewUrl,
}: {
  node: OutputNode;
  outcomeId?: string | null;
  previewUrl: string | null;
}) {
  if (previewUrl) {
    return (
      <iframe
        title="Static preview"
        sandbox={IFRAME_SANDBOX}
        src={previewUrl}
        className="flex-1 w-full bg-white"
      />
    );
  }
  if (node.content && node.content.trim()) {
    return (
      <iframe
        title={node.name}
        sandbox={IFRAME_SANDBOX}
        srcDoc={node.content}
        className="flex-1 w-full bg-white"
      />
    );
  }
  return <FetchedHtml node={node} outcomeId={outcomeId} />;
}

function FetchedHtml({ node, outcomeId }: { node: OutputNode; outcomeId?: string | null }) {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!outcomeId || !node.path) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    authFetch(artifactFileUrl(outcomeId, node.path))
      .then(async (res) => {
        if (!res.ok) throw new Error('missing');
        const text = await res.text();
        if (!cancelled) setHtml(text);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [outcomeId, node.path]);

  if (failed || (!html && !outcomeId)) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center text-texts px-6">
        <i className="ri-error-warning-line text-3xl mb-2" />
        <div className="text-sm text-textp">HTML is not available to preview</div>
      </div>
    );
  }
  if (!html) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center text-texts">
        Loading preview
      </div>
    );
  }
  return (
    <iframe
      title={node.name}
      sandbox={IFRAME_SANDBOX}
      srcDoc={html}
      className="flex-1 w-full bg-white"
    />
  );
}
